/* eslint-disable camelcase */
/* eslint-disable react/forbid-prop-types */
// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import Tooltip from 'rc-tooltip';
import Menu, { MenuItem } from 'rc-menu';
import { withApollo } from '@apollo/client/react/hoc';

// MaterialUI
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/Edit';
import CodeIcon from '@material-ui/icons/Code';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';

// app modules: actions
import { openModal } from 'actions/modals';

// app modules: components
import ActionSheet from 'components/ActionSheet';
import AddToFavouritesItem from 'components/AddToFavouritesItem';
import ShareItem, { ShareMenuItems, ShareMenu } from 'components/ShareItem';
import CollectionShareMenuItem from 'components/ShareItem/CollectionShareMenuItem';
import ManagePrivacyItem, {
  PrivacyMenuItems,
  ManagePrivacyMenu,
} from 'components/Collection/OptionsMenu/ManagePrivacyItem';

// app modules: props
import CollectionsProps from 'components/Collection/CollectionsProps';

// app modules: Queries
import MAILCHIMP_FORMAT_QUERY from 'components/Collection/MailchimpGraphQL';

// app modules: services
import assets from 'services/assets';
import route from 'services/route';
import history from 'services/history';

// app modules: constants
import { CONTENT_TYPES } from 'constants/index';
import { MODALS } from 'constants/view';

// assets
import CssOptionsMenu from './OptionsMenu.scss';

class CollectionOptionsMenu extends Component {
  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
    className: PropTypes.string,
    collection: CollectionsProps.collection.isRequired,
    displayType: PropTypes.string.isRequired,
    heading: PropTypes.string,
    image: PropTypes.node,
    loggedIn: PropTypes.bool,
    openModal: PropTypes.func.isRequired,
    shareImageUrl: PropTypes.string,
    styleButton: PropTypes.bool,
    subMenu: PropTypes.string,
    client: PropTypes.any,
  };

  static defaultProps = {
    className: '',
    heading: null,
    image: null,
    loggedIn: false,
    shareImageUrl: null,
    subMenu: null,
    styleButton: true,
    client: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      active: false,
      activeSubMenu: null,
      menuOpen: false,
      ModalComponent: null,
    };
  }

  handleOpen = () => {
    this.setState({ menuOpen: true, active: true, activeSubMenu: null });
  };

  handleClose = () => {
    this.setState({
      menuOpen: false,
    });
    // Once the transition has finished become inactive - we don't need to render until we are used again
    setTimeout(() => this.setState({ active: false, activeSubMenu: false }), 195);
  };

  openConfirmDeletionModal = () => {
    import('components/Collection/ConfirmDeletion/ConfirmDeletion.jsx' /* webpackChunkName: "collection-confirm-deletion", webpackMode: "lazy" */).then(
      module => {
        this.setState({
          menuOpen: false,
          ModalComponent: module.default,
        });
        this.props.openModal({
          name: MODALS.COLLECTION_CONFIRM_DELETION,
        });
      },
    );
  };

  handleDialogOpen = ModalComponent => {
    this.handleClose();
    this.setState({
      ModalComponent,
    });
  };

  shouldShowEmbed = () => {
    const { collection } = this.props;
    return !collection?.items_count || collection?.items_count?.contents > 0;
  };

  openSubMenu = subMenu => {
    // Side out
    this.setState({ menuOpen: false });
    // Render collections menu (off-screen)
    setTimeout(() => this.setState({ activeSubMenu: subMenu, active: true }), 195);
    // Slide back in
    setTimeout(() => this.setState({ menuOpen: true }), 300);
  };

  handleButtonClick = () => {
    const { menuOpen } = this.state;
    const { subMenu } = this.props;
    this.setState({ active: true, activeSubMenu: subMenu });
    setTimeout(() => this.setState({ menuOpen: !menuOpen }), 0);
  };

  formatForMailchimp = async (client, id) => {
    const { data } = await client.query({
      query: MAILCHIMP_FORMAT_QUERY,
      variables: { id },
    });

    if (!data || !data.DownloadMailchimpFormat) {
      return;
    }

    const blob = new Blob([data.DownloadMailchimpFormat], { type: 'text/html' });

    window.open(URL.createObjectURL(blob), '_blank');
  };

  renderEditItem(ItemComponent) {
    const { id, can_edit } = this.props.collection;
    if (can_edit) {
      return (
        <ItemComponent
          className="options-menu__item"
          button
          onClick={() => {
            this.handleClose();
            history.push(route.collection.edit(id));
          }}
        >
          <ListItemIcon>
            <EditIcon color="primary" fontSize="large" />
          </ListItemIcon>
          <ListItemText primary="Edit collection" />
        </ItemComponent>
      );
    }
    return null;
  }

  renderMailchimpItem(ItemComponent) {
    const { id, can_edit } = this.props.collection;
    if (can_edit) {
      return (
        <ItemComponent
          className="options-menu__item"
          button
          onClick={() => {
            this.handleClose();
            this.formatForMailchimp(this.props.client, id);
          }}
        >
          <ListItemIcon>
            <CodeIcon color="primary" fontSize="large" />
          </ListItemIcon>
          <ListItemText primary="Format for Mailchimp" />
        </ItemComponent>
      );
    }
    return null;
  }

  renderDeleteItem(ItemComponent) {
    const { can_edit } = this.props.collection;

    if (can_edit) {
      return (
        <ItemComponent
          className="options-menu__item"
          button
          onClick={this.openConfirmDeletionModal}
        >
          <ListItemIcon>
            <DeleteIcon color="primary" fontSize="large" />
          </ListItemIcon>
          <ListItemText primary="Delete collection" />
        </ItemComponent>
      );
    }
    return null;
  }

  renderPersonItem(ItemComponent) {
    const { person_id, display_name } = this.props.collection;

    if (person_id && display_name) {
      return (
        <ItemComponent
          className="options-menu__item"
          button
          onClick={() => {
            this.handleClose();
            history.push(route.person(person_id));
          }}
        >
          <ListItemAvatar>
            <Avatar
              src={assets.avatarUrl(person_id, {
                resize: 'fit-in',
                width: 100,
                height: 100,
              })}
              alt={display_name}
            />
          </ListItemAvatar>
          <ListItemText primary={`Go to ${display_name}`} />
        </ItemComponent>
      );
    }
    return null;
  }

  renderMenuItems(ItemComponent, extraProps = {}) {
    const { collection, shareImageUrl } = this.props;
    const { id } = collection;
    return (
      <React.Fragment>
        <ShareItem
          className="options-menu__item"
          ItemComponent={ItemComponent}
          WrapperComponent={CollectionShareMenuItem}
          collection={collection}
          shareImageUrl={shareImageUrl}
          onClick={() => this.openSubMenu('share')}
          onComplete={this.handleClose}
          onDialogOpen={this.handleDialogOpen}
          showEmbed={this.shouldShowEmbed()}
          {...extraProps}
        />
        <AddToFavouritesItem
          className="options-menu__item"
          component={ItemComponent}
          itemId={String(id)}
          contentType={CONTENT_TYPES.COLLECTION}
          onClick={this.handleClose}
          {...extraProps}
        />
        <ManagePrivacyItem
          ItemComponent={ItemComponent}
          collection={collection}
          onClick={() => this.openSubMenu('privacy')}
          onSubMenuItemClick={this.handleClose}
          {...extraProps}
        />
        {this.renderEditItem(ItemComponent, extraProps)}
        {this.renderMailchimpItem(ItemComponent, extraProps)}
        {this.renderDeleteItem(ItemComponent, extraProps)}
        {this.renderPersonItem(ItemComponent, extraProps)}
      </React.Fragment>
    );
  }

  renderPopupMenu = () => {
    const { subMenu, collection, shareImageUrl } = this.props;
    switch (subMenu) {
      case 'privacy':
        return (
          <ManagePrivacyMenu
            popup
            ItemComponent={MenuItem}
            collection={collection}
            onClick={this.handleClose}
          />
        );
      case 'share':
        return (
          <ShareMenu
            popup
            className="options-menu__item"
            ItemComponent={MenuItem}
            WrapperComponent={CollectionShareMenuItem}
            collection={collection}
            shareImageUrl={shareImageUrl}
            onClick={this.handleClose}
            onComplete={this.handleClose}
            onDialogOpen={this.handleDialogOpen}
          />
        );
      default:
        return <Menu selectable={false}>{this.renderMenuItems(MenuItem, { popup: true })}</Menu>;
    }
  };

  renderActionSheetItems = () => {
    const { activeSubMenu } = this.state;
    const { collection, shareImageUrl } = this.props;
    switch (activeSubMenu) {
      case 'privacy':
        return (
          <PrivacyMenuItems
            ItemComponent={ListItem}
            collection={collection}
            onClick={this.handleClose}
          />
        );
      case 'share':
        return (
          <ShareMenuItems
            className="options-menu__item"
            ItemComponent={ListItem}
            WrapperComponent={CollectionShareMenuItem}
            collection={collection}
            shareImageUrl={shareImageUrl}
            onClick={this.handleClose}
            onComplete={this.handleClose}
            onDialogOpen={this.handleDialogOpen}
            showEmbed={this.shouldShowEmbed()}
          />
        );
      default:
        return <List>{this.renderMenuItems(ListItem, { button: true, popup: false })}</List>;
    }
  };

  render() {
    const { active, activeSubMenu, ModalComponent, menuOpen } = this.state;
    const {
      children,
      collection,
      displayType,
      image,
      heading,
      loggedIn,
      className,
      styleButton,
      subMenu,
    } = this.props;

    if (!loggedIn && !subMenu) {
      return null;
    }
    const dataCy = subMenu ? `collection-options-button-${subMenu}` : 'collection-options-button';
    const buttonId = subMenu
      ? `collection-options-button-${subMenu}-${collection.id}`
      : `collection-options-button-${collection.id}`;
    const button = styleButton ? (
      <Button
        data-cy={dataCy}
        id={buttonId}
        size="medium"
        className={className}
        onClick={this.handleButtonClick}
      >
        {children}
      </Button>
    ) : (
      <button data-cy={dataCy} id={buttonId} className={className} onClick={this.handleButtonClick}>
        {children}
      </button>
    );
    const modalEl = ModalComponent ? (
      <ModalComponent
        open
        collection={collection}
        onClose={() => this.setState({ ModalComponent: null })}
      />
    ) : null;
    if (!active) {
      // Don't render the menu until the user has clicked on it
      return (
        <React.Fragment>
          {modalEl}
          {button}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {modalEl}
        {displayType === 'mobile' ? (
          <React.Fragment>
            {button}
            <ActionSheet
              withCloseButton
              itemRenderer={this.renderActionSheetItems}
              heading={activeSubMenu ? null : heading}
              image={activeSubMenu ? null : image}
              visible={menuOpen}
              onOpen={this.handleOpen}
              onClose={this.handleClose}
            />
          </React.Fragment>
        ) : (
          <Tooltip
            visible={menuOpen}
            trigger={['click']}
            overlay={<div>{this.renderPopupMenu()}</div>}
            placement="bottomRight"
            overlayClassName="rc-tooltip--plain"
            onVisibleChange={isOpen => {
              this.setState({ menuOpen: isOpen });
            }}
          >
            {button}
          </Tooltip>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  baseUrl: state.config.baseUrl,
  displayType: state.dimensions.displayType,
  loggedIn: state.user.logged_in,
});

const mapDispatchToProps = {
  openModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withApollo(withStyles(CssOptionsMenu)(CollectionOptionsMenu)));
